import React, { useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { MinusIcon, PlusIcon } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useFunnel } from '../../contexts/FunnelContext';
import { useSystemSelection } from '../../contexts/SystemSelectionContext';
import { formatMoney, priceBundle } from '../../utils/pricing';
import { planForPositions } from '../../utils/positions';
import { goToShop } from '../../utils/shopIntent';
import { buttonClasses } from '../../utils/buttonStyles';
import { track } from '../../utils/analytics';
import { WarrantyTag } from '../ui/WarrantyTag';
const MIN = 1;
const MAX = 12;
const QUICK = [2, 3, 4, 6, 12];

/** "How many lighting positions do you want?" — recommends installed + backup configurations. */
export function PositionsCalculator() {
  const {
    lang
  } = useLanguage();
  const reduce = useReducedMotion();
  const {
    startSystem
  } = useFunnel();
  const {
    setSelectedId
  } = useSystemSelection();
  const [positions, setPositions] = useState(4);
  const [withBackups, setWithBackups] = useState(true);
  const m = (n: number) => formatMoney(n, lang);
  const plan = planForPositions(positions, withBackups);
  const b = plan.bundle;
  const p = priceBundle(b);
  const badge = b.badge === 'mostPopular' ? 'Most popular' : b.badge === 'bestValue' ? 'Best value' : null;
  const set = (n: number) => setPositions(Math.max(MIN, Math.min(MAX, n)));
  return <div className="grid gap-8 lg:grid-cols-[1fr_1.1fr] lg:items-center lg:gap-12" data-id="element-882" data-mp="2g40buqxqlvm">
      <div data-id="element-883" data-mp="gfc3eyq7ye53">
        <p className="font-display text-sm font-semibold uppercase tracking-[0.16em] text-titanium" data-id="element-884" data-mp="gfv99h2lhc2r">Lighting positions</p>
        <div className="mt-3 flex items-center gap-4" data-id="element-885" data-mp="bf7jszu0l3an">
          <button type="button" onClick={() => set(positions - 1)} disabled={positions <= MIN} aria-label="Fewer positions" className="flex h-14 w-14 items-center justify-center rounded-md border border-white/20 text-white transition-colors duration-150 hover:border-marine disabled:opacity-30" data-id="element-886" data-mp="1ruiks2ux7mlb">
            <MinusIcon className="h-5 w-5" aria-hidden data-id="element-887" data-mp="pfjfxwo4o9e6" />
          </button>
          <output aria-live="polite" className="w-20 text-center font-display text-7xl font-semibold leading-none text-white" data-id="element-888" data-mp="tjr6lze5bzf1">
            {positions}
          </output>
          <button type="button" onClick={() => set(positions + 1)} disabled={positions >= MAX} aria-label="More positions" className="flex h-14 w-14 items-center justify-center rounded-md border border-white/20 text-white transition-colors duration-150 hover:border-marine disabled:opacity-30" data-id="element-889" data-mp="1rs2gpqupqbb">
            <PlusIcon className="h-5 w-5" aria-hidden data-id="element-890" data-mp="fkzv8kqo4rul" />
          </button>
        </div>

        <div className="mt-6 flex flex-wrap gap-2" role="group" aria-label="Common choices" data-id="element-891" data-mp="3e8n82sldxxgm">
          {QUICK.map(n => <button key={n} type="button" onClick={() => set(n)} aria-pressed={positions === n} className={`h-11 min-w-[3rem] rounded-md border px-4 font-display text-lg font-semibold transition-colors duration-150 ${positions === n ? 'border-marine bg-marine text-white' : 'border-white/15 text-titanium hover:border-marine/60 hover:text-white'}`} data-id="element-892" data-mp="3boh4im0oo90l">
              {n}
            </button>)}
        </div>

        <label className="mt-6 flex cursor-pointer items-start gap-3" data-id="element-893" data-mp="2nymsg6o6uohe">
          <input type="checkbox" checked={withBackups} onChange={e => setWithBackups(e.target.checked)} className="mt-1 h-5 w-5 shrink-0 accent-[#248DFF]" data-id="element-894" data-mp="2iztfcfctczhr" />
          <span data-id="element-895" data-mp="29zn4z2cewfmt">
            <span className="block font-semibold text-white" data-id="element-896" data-mp="29y3mr6773uie">Include a charged backup set</span>
            <span className="block text-sm text-titanium" data-id="element-897" data-mp="24zsgf5g4024x">Optional. One set installed, one set ready — swap instead of waiting to recharge.</span>
          </span>
        </label>

        {/* Position map: installed vs backup */}
        <div className="mt-6" aria-hidden data-id="element-898" data-mp="cxzi45b30p7u">
          <div className="flex flex-wrap gap-1.5" data-id="element-899" data-mp="3vcqhb6g5qzzz">
            {Array.from({
            length: b.qty
          }, (_, i) => <motion.span key={`${b.id}-${i}`} initial={reduce ? false : {
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.2,
            delay: reduce ? 0 : Math.min(i * 0.02, 0.3),
            ease: [0.23, 1, 0.32, 1]
          }} className={`h-6 w-6 rounded-full border-2 ${i < plan.installed ? 'border-marine bg-marine/30' : 'border-ice/50 bg-transparent'}`} data-id="element-900" data-mp="45nq4f0bktpr" />)}
          </div>
          <p className="mt-3 flex gap-5 text-xs uppercase tracking-[0.12em] text-titanium" data-id="element-901" data-mp="1amj2blpo8fu3">
            <span className="inline-flex items-center gap-1.5" data-id="element-902" data-mp="1fl2nhjwfxnle">
              <span className="h-3 w-3 rounded-full border-2 border-marine bg-marine/30" data-id="element-903" data-mp="1fkkqum5v97cy" /> Installed
            </span>
            <span className="inline-flex items-center gap-1.5" data-id="element-904" data-mp="1pmwxqj6laz9m">
              <span className="h-3 w-3 rounded-full border-2 border-ice/50" data-id="element-905" data-mp="1krrc7lh3nmm2" /> Charged & ready
            </span>
          </p>
        </div>
      </div>

      <AnimatePresence mode="wait" initial={false} data-id="element-906" data-mp="1tr4k49pks34o">
        <motion.div key={`${b.id}-${positions}-${withBackups}`} initial={{
        opacity: 0,
        y: 6
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.18,
        ease: [0.23, 1, 0.32, 1]
      }} className="mf-hud border border-marine bg-[#0D2440] p-5 md:p-8" data-id="element-907" data-mp="1tpbaqmnkv4sz">
          <div className="flex items-start gap-5" data-id="element-908" data-mp="2df4z0qr331sg">
            <img src={b.image} alt={`${b.name} — ${b.qty} M1 PRO ${b.qty === 1 ? 'light' : 'lights'}`} width={1122} height={1402} className="block aspect-[1122/1402] w-[112px] shrink-0 border border-white/10 object-cover sm:w-[150px]" data-id="element-909" data-mp="2dfdbihjao534" />
            <div className="min-w-0" data-id="element-910" data-mp="2i7p0q82wv01w">
              <div className="flex flex-wrap items-center gap-x-3" data-id="element-911" data-mp="2n45o302adadx">
                <p className="font-display text-sm font-semibold uppercase tracking-[0.16em] text-titanium" data-id="element-912" data-mp="2n6icnw2juizy">Recommended</p>
                {badge && <p className="text-xs font-semibold uppercase tracking-[0.16em] text-ice" data-id="element-913" data-mp="2x3eg3ndlcexn">{badge}</p>}
              </div>
              <h3 className="mt-2 font-display text-2xl font-semibold uppercase leading-none tracking-[0.03em] sm:text-3xl" data-id="element-914" data-mp="313ou9nbjebnl">
                {b.tagline}
                <span className="mt-1 block text-lg text-titanium sm:text-xl" data-id="element-915" data-mp="3au9okyk6gu0p">
                  {b.qty} M1 PRO {b.qty === 1 ? 'light' : 'lights'}
                </span>
              </h3>
              <ul className="mt-3 space-y-0.5 text-base sm:text-lg" data-id="element-916" data-mp="3fv74t5lmndzk">
                <li className="text-white" data-id="element-917" data-mp="3ku0ndcfrtv6l">
                  <span className="font-semibold" data-id="element-918" data-mp="3otrjzwrzb5j1">{plan.installed}</span> installed
                </li>
                {plan.backups > 0 && <li className="text-ice" data-id="element-919" data-mp="1d7gycu8oblw6">
                    <span className="font-semibold" data-id="element-920" data-mp="1953te81r3s0g">{plan.backups}</span> {withBackups ? 'charged backups' : 'extra lights as spares'}
                  </li>}
              </ul>
            </div>
          </div>
          <div data-id="element-921" data-mp="z7fwbbgoa8f4">
            {!plan.exact && <p className="mt-3 text-sm text-titanium" data-id="element-922" data-mp="z8r19elutm7t">
                {withBackups ? `${positions} positions need ${positions * 2} lights with a full backup set. The ${b.tagline} is the closest official system, giving you ${plan.backups} charged backups.` : `The ${b.tagline} is the closest official system for ${positions} position${positions === 1 ? '' : 's'}.`}
              </p>}
            <div className="mt-6 flex flex-wrap items-baseline gap-x-4 gap-y-1" data-id="element-923" data-mp="2fkwdj3i1n5na">
              <span className="font-display text-5xl font-semibold leading-none" data-id="element-924" data-mp="2fmzeujoncl7b">{m(p.total)}</span>
              {p.savings > 0 && <span className="text-sm font-semibold uppercase tracking-[0.08em] text-ice" data-id="element-925" data-mp="1j74tfuvykuez">Save {m(p.savings)}</span>}
            </div>
            <p className="mt-1 text-sm text-titanium" data-id="element-926" data-mp="1nyd4swgumpus">{m(p.perUnit)} per light</p>
            <div className="mt-3" data-id="element-927" data-mp="1a6zn7o2i422e">
              <WarrantyTag tier={b.warranty} linked data-id="element-928" data-mp="1a81b4t4m8j1j" />
            </div>
            <div className="mt-6 flex flex-col gap-3" data-id="element-929" data-mp="1e8tlxqt4yvzx">
              <button type="button" onClick={() => {
              track('positions_calculator_add', {
                positions,
                withBackups,
                bundleId: b.id,
                value: p.total,
                qty: b.qty
              });
              setSelectedId(b.id);
              startSystem(b.id);
            }} className={`${buttonClasses('primary', 'lg')} w-full`} data-id="element-930" data-mp="1e5fl4n04euul">
                Add to cart — {m(p.total)}
              </button>
              <button type="button" onClick={() => goToShop(b.id)} className="h-11 w-full font-display text-[15px] font-semibold uppercase tracking-[0.12em] text-titanium underline decoration-white/20 underline-offset-4 transition-colors duration-150 hover:text-white hover:decoration-ice" data-id="element-931" data-mp="hgpwmdtzn3zn">
                Compare all systems
              </button>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>;
}